/**
 * Model connections — list, add, edit, delete (SPEC.md §6.2, D182, D211).
 *
 * Every account can add their own; the list also carries the admin's. Edit/Delete only
 * render on a row this account actually owns (`config.created_by === me.id`) — a
 * server-enforced boundary (`routers/models.py`), this is just not offering a control that
 * would 404 anyway. A row that isn't the caller's own reads "· shared" instead, since in
 * this list that can only ever mean the admin's free pool, never another friend's.
 *
 * Edit expands the row in place, `TaskEditor`-style: no drawer, no form library, plain
 * controlled inputs. `apiKey` is never pre-filled from an existing row — the server never
 * returns one (`has_api_key` is all it says) — so editing a config with a key already set
 * shows a placeholder instead, and leaving the field blank means "leave it as is" rather
 * than "there is no key". "Clear stored key" is its own explicit action for the one case a
 * blank field can't express.
 */

import { useEffect, useState } from "react";
import {
  api,
  type Me,
  type ModelConfig,
  type ModelConfigInput,
  type ModelProvider,
  type VramClass,
} from "../api";
import { Pencil, Trash } from "./icons";
import { useModelConfigs } from "../modelConfigs";
import { useAutoGrow } from "./useAutoGrow";

const PROVIDERS: { id: ModelProvider; label: string }[] = [
  { id: "openai_compatible", label: "OpenAI-compatible (LM Studio, Ollama, vLLM…)" },
  { id: "anthropic", label: "Anthropic" },
];

const VRAM_CLASSES: { id: VramClass; label: string }[] = [
  { id: "none", label: "None - remote, always available" },
  { id: "light", label: "Light - coexists with other GPU use" },
  { id: "heavy", label: "Heavy - needs the card to itself" },
];

type FormState = {
  name: string;
  provider: ModelProvider;
  modelKey: string;
  baseUrl: string;
  apiKey: string;
  clearApiKey: boolean;
  vramClass: VramClass;
  ttlSeconds: string;
  notes: string;
};

const BLANK_FORM: FormState = {
  name: "",
  provider: "openai_compatible",
  modelKey: "",
  baseUrl: "",
  apiKey: "",
  clearApiKey: false,
  vramClass: "none",
  ttlSeconds: "",
  notes: "",
};

function formFor(config: ModelConfig): FormState {
  return {
    name: config.name,
    provider: config.provider,
    modelKey: config.model_key,
    baseUrl: config.base_url ?? "",
    apiKey: "",
    clearApiKey: false,
    vramClass: config.vram_class,
    ttlSeconds: config.ttl_seconds ? String(config.ttl_seconds) : "",
    notes: config.notes ?? "",
  };
}

/** The fields shared by add and edit, `TaskEditor`-style: one form, reused for both. */
function ModelConfigForm({
  config,
  onSave,
  onCancel,
}: {
  /** Omitted in add mode; present in edit mode, prefilling the fields it belongs to. */
  config?: ModelConfig;
  onSave: (body: Partial<ModelConfigInput>) => Promise<void>;
  onCancel?: () => void;
}) {
  const [form, setForm] = useState<FormState>(config ? formFor(config) : BLANK_FORM);
  const [error, setError] = useState<string | null>(null);
  const [discovered, setDiscovered] = useState<string[]>([]);
  const [discovering, setDiscovering] = useState(false);
  const [discoverError, setDiscoverError] = useState<string | null>(null);
  const notesRef = useAutoGrow(form.notes);

  async function discover() {
    const baseUrl = form.baseUrl.trim();
    if (!baseUrl) return;
    setDiscovering(true);
    setDiscoverError(null);
    try {
      const result = await api.discoverModels(baseUrl, form.apiKey.trim() || undefined);
      setDiscovered(result.models);
      // Reflect the same /v1 correction the server applies before storing (D182 follow-
      // up), so the field shows what will actually be saved rather than what was typed.
      setForm((f) => ({ ...f, baseUrl: result.base_url }));
    } catch (err) {
      setDiscovered([]);
      setDiscoverError(err instanceof Error ? err.message : String(err));
    } finally {
      setDiscovering(false);
    }
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!form.name.trim() || !form.modelKey.trim()) return;

    const shared = {
      name: form.name.trim(),
      provider: form.provider,
      model_key: form.modelKey.trim(),
      base_url: form.baseUrl.trim() || null,
      vram_class: form.vramClass,
      ttl_seconds: form.ttlSeconds.trim() ? Number(form.ttlSeconds) : null,
      notes: form.notes.trim() || null,
    };

    try {
      if (config) {
        const body: Partial<ModelConfigInput> = { ...shared };
        if (form.clearApiKey) body.api_key = "";
        else if (form.apiKey.trim()) body.api_key = form.apiKey.trim();
        await onSave(body);
      } else {
        await onSave({ ...shared, api_key: form.apiKey.trim() || undefined });
        setForm(BLANK_FORM);
        setDiscovered([]);
        setDiscoverError(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <form onSubmit={submit} style={{ display: "grid", gap: "0.6rem" }}>
      {error && <p className="error">{error}</p>}

      <input
        className="input"
        placeholder="Name - e.g. Local light, Claude"
        aria-label="Name"
        value={form.name}
        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        autoFocus={!!config}
      />

      <div className="row wrap">
        {PROVIDERS.map((p) => (
          <label key={p.id} className="row small" title={p.label}>
            <input
              type="radio"
              name="provider"
              checked={form.provider === p.id}
              onChange={() => setForm((f) => ({ ...f, provider: p.id }))}
            />
            {p.label}
          </label>
        ))}
      </div>

      {form.provider === "openai_compatible" && (
        <div className="row small">
          <input
            className="input"
            placeholder="Base URL - e.g. http://colossus:1234/v1"
            aria-label="Base URL"
            value={form.baseUrl}
            onChange={(e) => setForm((f) => ({ ...f, baseUrl: e.target.value }))}
          />
          <button
            type="button"
            className="btn ghost small"
            disabled={!form.baseUrl.trim() || discovering}
            onClick={() => void discover()}
          >
            {discovering ? "Listing…" : "List models"}
          </button>
        </div>
      )}

      {discoverError && <p className="small error">{discoverError}</p>}

      {discovered.length > 0 ? (
        // Same shape as DestinationPicker (TaskFields.tsx): a plain `select.input`, a
        // first row that names the empty state rather than pre-selecting a guess. The
        // original text-input-plus-datalist version filled the first result in
        // silently and gave no visual sign there was a dropdown at all — reported
        // directly.
        <select
          className="input"
          aria-label="Model key"
          value={form.modelKey}
          onChange={(e) => setForm((f) => ({ ...f, modelKey: e.target.value }))}
        >
          <option value="">Select a model…</option>
          {form.modelKey && !discovered.includes(form.modelKey) && (
            <option value={form.modelKey}>{form.modelKey} (current)</option>
          )}
          {discovered.map((id) => (
            <option key={id} value={id}>
              {id}
            </option>
          ))}
        </select>
      ) : (
        <input
          className="input"
          placeholder="Model key - the exact string the provider expects"
          aria-label="Model key"
          value={form.modelKey}
          onChange={(e) => setForm((f) => ({ ...f, modelKey: e.target.value }))}
        />
      )}

      {config?.has_api_key && !form.clearApiKey ? (
        <div className="row small">
          <input
            className="input"
            type="password"
            placeholder="•••••••• (unchanged - type to replace)"
            aria-label="API key"
            value={form.apiKey}
            onChange={(e) => setForm((f) => ({ ...f, apiKey: e.target.value }))}
          />
          <button
            type="button"
            className="btn ghost small"
            onClick={() => setForm((f) => ({ ...f, clearApiKey: true, apiKey: "" }))}
          >
            Clear stored key
          </button>
        </div>
      ) : (
        <input
          className="input"
          type="password"
          placeholder={
            form.clearApiKey ? "Cleared on save" : "API key - optional, for hosted providers"
          }
          aria-label="API key"
          value={form.apiKey}
          disabled={form.clearApiKey}
          onChange={(e) => setForm((f) => ({ ...f, apiKey: e.target.value }))}
        />
      )}

      <div className="row wrap">
        {VRAM_CLASSES.map((v) => (
          <label key={v.id} className="row small" title={v.label}>
            <input
              type="radio"
              name="vram_class"
              checked={form.vramClass === v.id}
              onChange={() => setForm((f) => ({ ...f, vramClass: v.id }))}
            />
            {v.label}
          </label>
        ))}
      </div>

      {form.vramClass !== "none" && (
        <input
          className="input"
          type="number"
          min={1}
          placeholder="Idle eviction, seconds (optional)"
          aria-label="TTL seconds"
          value={form.ttlSeconds}
          onChange={(e) => setForm((f) => ({ ...f, ttlSeconds: e.target.value }))}
        />
      )}

      <textarea
        ref={notesRef}
        className="input notes"
        placeholder="Notes - why this one exists (optional)"
        aria-label="Notes"
        rows={2}
        value={form.notes}
        onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
      />

      <div className="row wrap">
        <button
          className="btn primary small"
          type="submit"
          disabled={!form.name.trim() || !form.modelKey.trim()}
        >
          {config ? "Save" : "Add"}
        </button>
        {onCancel && (
          <button type="button" className="btn ghost small" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

export function ModelConfigsCard({
  refreshKey,
  onChange,
}: {
  refreshKey?: number;
  onChange?: () => void | Promise<void>;
}) {
  const { configs, error: loadError, reload } = useModelConfigs(refreshKey);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  // Whether the current account may edit or delete a given row (D211) — the list already
  // only ever contains the caller's own connections plus the admin's, so this is purely
  // "which of those two is it," not a second permission check the server doesn't also make.
  const [me, setMe] = useState<Me | null>(null);

  useEffect(() => {
    void api.me().then(setMe);
  }, []);

  async function remove(config: ModelConfig) {
    if (!window.confirm(`Delete "${config.name}"? This cannot be undone.`)) return;
    try {
      await api.deleteModelConfig(config.id);
      if (editingId === config.id) setEditingId(null);
      await reload();
      await onChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <>
      <section className="panel">
        <header>
          <h2>Add a model connection</h2>
          <span className="muted small">Local or hosted - a row per model you might use</span>
        </header>

        {loadError && <p className="error">{loadError}</p>}

        <ModelConfigForm
          onSave={async (body) => {
            await api.createModelConfig(body as ModelConfigInput);
            await reload();
            await onChange?.();
          }}
        />
      </section>

      <section className="panel">
        <header>
          <h2>Model connections</h2>
          <span className="muted small">{configs.length} configured</span>
        </header>

        {error && <p className="error">{error}</p>}

        {configs.length === 0 ? (
          <p className="muted small">
            Nothing yet - add a local runner (LM Studio, Ollama…) or a hosted provider above.
          </p>
        ) : (
          <table className="tokens">
            <thead>
              <tr>
                <th>Name</th>
                <th>Provider</th>
                <th>Model key</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {configs.map((config) => {
                const mine = me != null && config.created_by === me.id;
                return editingId === config.id ? (
                  <tr key={config.id}>
                    <td colSpan={4}>
                      <ModelConfigForm
                        config={config}
                        onSave={async (body) => {
                          await api.updateModelConfig(config.id, body);
                          setEditingId(null);
                          await reload();
                          await onChange?.();
                        }}
                        onCancel={() => setEditingId(null)}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr key={config.id}>
                    <td>{config.name}</td>
                    <td className="small">
                      {config.provider}
                      {config.has_api_key && <span className="muted"> · key set</span>}
                      {/* Only ever someone else's when it's the admin's own shared, free
                          connection - the list never includes another friend's (D211). */}
                      {!mine && <span className="muted"> · shared</span>}
                    </td>
                    <td className="small muted">{config.model_key}</td>
                    <td>
                      {mine && (
                        <>
                          <button
                            className="btn ghost small"
                            onClick={() => setEditingId(config.id)}
                            aria-label={`Edit: ${config.name}`}
                            title="Edit"
                          >
                            <Pencil />
                          </button>{" "}
                          <button
                            className="btn ghost small danger"
                            onClick={() => void remove(config)}
                            aria-label={`Delete: ${config.name}`}
                            title="Delete"
                          >
                            <Trash />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}
